// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage PRODUCTION-CUTTING-PLANNER-1 · Cutting Planner API client.
// Base: /api/erp/production/cutting-plans. Mirrors the workOrdersApi axios +
// unwrap() pattern. The XLS export is fetched THROUGH the axios client (so the
// in-memory Bearer token is attached) and downloaded as a Blob — the same
// auth-safe pattern used by the breakage slip.
// ════════════════════════════════════════════════════════════════════

import client from '../../../../api/client';

const BASE = '/api/erp/production/cutting-plans';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined ? resp.data.data : (resp ? resp.data : null);
}
const enc = encodeURIComponent;

export const cuttingPlannerApi = {
  nextOptNo: () => client.get(`${BASE}/next-opt-no`).then(unwrap),

  // Pending work orders — server-paginated. Filters: {page,pageSize,search}.
  pendingOrders: (filters = {}) =>
    client.get(`${BASE}/pending-orders`, { params: toParams(filters) }).then(unwrap),

  // Available STOCK groups for the selected WOs → [{ value, label, pieces }].
  stockGroups: (workOrderNos) =>
    client.post(`${BASE}/stock-groups`, { work_order_nos: workOrderNos }).then(unwrap),

  // Import every available piece cut from the chosen stock (value from stockGroups).
  importItems: (workOrderNos, stockKey) =>
    client.post(`${BASE}/import-items`, { work_order_nos: workOrderNos, stock_key: stockKey }).then(unwrap),

  // Tracker rows (one per plan, with completion stats). Filters: {dateFrom,dateTo,optNo}.
  trackerRows: (filters = {}) =>
    client.get(`${BASE}/tracker-rows`, { params: toParams(filters) }).then(unwrap),

  list: (filters = {}) =>
    client.get(`${BASE}/`, { params: toParams(filters) }).then(unwrap),

  planItems: (planId) => client.get(`${BASE}/${enc(planId)}/items`).then(unwrap),

  // payload: { planId?, planDate, glassType?, thickness?, items:[{sourcePieceId}] }
  save: (payload) => client.post(`${BASE}/`, payload).then(unwrap),

  remove: (planId) => client.delete(`${BASE}/${enc(planId)}`).then(unwrap),

  // Stage CUTTING-PLANNER-STOCK-ISSUE — deduct raw sheets consumed by a plan
  // from on-hand stock (manual, hard-blocked, reversible).
  // → { plan, planStocks[], candidates[], issues[] }
  availableStock: (planId) => client.get(`${BASE}/${enc(planId)}/stock`).then(unwrap),
  // payload: { current_stock_id, sheets, remarks? }
  issueStock: (planId, payload) => client.post(`${BASE}/${enc(planId)}/stock/issue`, payload).then(unwrap),
  reverseIssue: (planId, issueId) =>
    client.post(`${BASE}/${enc(planId)}/stock/issues/${enc(issueId)}/reverse`).then(unwrap),

  // Off-cut recording context — the glass consumed by the plan (for the
  // "Record off-cuts" modal). → { plan, glassOptions[] }
  offcutContext: (planId) => client.get(`${BASE}/${enc(planId)}/offcut-context`).then(unwrap),

  // Per plan × thickness utilization report. Filters: {dateFrom,dateTo,thickness,search,page,pageSize}.
  // → { rows[], total, limit, offset, totals }
  utilizationReport: (filters = {}) =>
    client.get(`${BASE}/utilization-report`, { params: toReportParams(filters) }).then(unwrap),

  // Export → download the .xls (fetched with the auth header attached).
  exportXls: async (payload) => {
    const resp = await client.post(`${BASE}/export-xls`, payload, { responseType: 'blob' });
    const filename = filenameFrom(resp) || 'cutting-plan.xls';
    triggerDownload(resp.data, filename);
    return filename;
  }
};

function toParams(f) {
  const p = {};
  if (f.dateFrom) p.date_from = f.dateFrom;
  if (f.dateTo) p.date_to = f.dateTo;
  if (f.optNo != null && String(f.optNo).trim() !== '') p.opt_no = f.optNo;
  if (f.thickness != null && String(f.thickness).trim() !== '') p.thickness = f.thickness;
  if (f.search != null && String(f.search).trim() !== '') p.search = String(f.search).trim();
  if (f.page != null) p.page = f.page;
  if (f.pageSize != null) p.page_size = f.pageSize;
  if (f.withSummary === false) p.with_summary = '0';   // skip the page-independent KPI aggregate
  return p;
}

function toReportParams(f) {
  const p = {};
  if (f.dateFrom) p.fromDate = f.dateFrom;
  if (f.dateTo) p.toDate = f.dateTo;
  if (f.thickness != null && String(f.thickness).trim() !== '') p.thickness = f.thickness;
  if (f.category != null && String(f.category).trim() !== '') p.category = String(f.category).trim();
  if (f.brand != null && String(f.brand).trim() !== '') p.brand = String(f.brand).trim();
  if (f.search != null && String(f.search).trim() !== '') p.search = String(f.search).trim();
  if (f.limit != null) p.limit = f.limit;
  if (f.offset != null) p.offset = f.offset;
  return p;
}

function filenameFrom(resp) {
  const cd = resp && resp.headers && (resp.headers['content-disposition'] || resp.headers['Content-Disposition']);
  if (!cd) return null;
  const m = /filename="?([^"]+)"?/.exec(cd);
  return m ? m[1] : null;
}

function triggerDownload(blobData, filename) {
  const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default cuttingPlannerApi;
